import { neverCancel } from '@mimir/common/async/async';
import { makePriority, PrioritySemaphore } from '@mimir/common/async/prioritySemaphore';
import { invariant } from '@mimir/common/util/invariant';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { VideoApiActions } from './apiActions';
import type { UrlArrayResponseDto } from '@mimir/common/dto/urlArrayDtos';
import type { SegmentUrlResolver } from './segmentUrlCache';


export interface ItemSegmentUrlResolver {
  getMediaSegmentUrl(segmentIndex: number): Promise<string>;
}


/**
 * This class implements a url cache for specific stream of a specific item.
 */
export class ItemSegmentUrlCache implements ItemSegmentUrlResolver {
  private semaphore = new PrioritySemaphore(1);
  private urlBlocks: UrlArrayResponseDto[] = [];

  constructor(
    private api: VideoApiActions,
    private itemId: string,
    private method: 'get' | 'put',
    private streamId: StreamRoleId,
    private batchSize: number,

  ) {}

  getMediaSegmentUrl(index: number): Promise<string> {
    /*
      Make sure we don't needlessly request the same information multiple times
      due to concurrency.
    */
    return this.semaphore.limit(() => this.getMediaSegmentUrlCore(index), makePriority(), neverCancel);
  }


  private tryGetUrl(index: number): string | undefined {
    const blockIndex = Math.floor(index / this.batchSize);
    if (this.urlBlocks[blockIndex] === undefined) {
      return undefined;
    }
    const block = this.urlBlocks[blockIndex]!; // Safe after undefined check above
    const offset = index - block.first;
    invariant(offset >= 0 && offset < block.urlSuffixes.length,
      () => `Index ${index} outside bounds of block ${blockIndex} [${block.first};${block.first + block.urlSuffixes.length - 1}]`);
    return `${block.prefix}${block.urlSuffixes[offset]}`;
  }


  private async getMediaSegmentUrlCore(
    index: number
  ): Promise<string> {
    let url = this.tryGetUrl(index);
    if (url !== undefined) {
      return url;
    }

    const blockIndex = Math.floor(index / this.batchSize);
    this.urlBlocks[blockIndex] = await this.api.getUrlArray({
      itemId: this.itemId,
      method: this.method,
      streamId: this.streamId,
      first: blockIndex * this.batchSize,
      last: (blockIndex + 1) * this.batchSize - 1,
    });

    url = this.tryGetUrl(index);
    invariant(
      url != undefined,
      'Missing url for segment index ' + index + ' of stream ' + this.streamId
    );
    return url;
  }
}


/**
 * This class is a wrapper around ItemSegmentUrlCache objects that implements
 * the SegmentUrlResolver interface for drop-in replacement of SegmentUrlCache.
 */
export class SegmentUrlCache2 implements SegmentUrlResolver {

  private caches: Record<string, ItemSegmentUrlCache> = {};

  constructor(
    private api: VideoApiActions,
    private method: 'get' | 'put',
    private batchSize: number,
  ) {}



  getMediaSegmentUrl(itemId: string, streamId: StreamRoleId, segmentIndex: number): Promise<string> {
    const key = `${itemId}:${streamId}`;
    if (!this.caches[key]) {
      this.caches[key] = new ItemSegmentUrlCache(this.api, itemId, this.method, streamId, this.batchSize);
    }

    return this.caches[key].getMediaSegmentUrl(segmentIndex);
  }

}

/**
 * Optimized URL cache that fetches blocks in pairs with background loading.
 * Pre-fetches all remaining segments based on total segment count after initial access.
 */
export class OptimizedItemSegmentUrlCache implements ItemSegmentUrlResolver {
  private blocks: (UrlArrayResponseDto | undefined)[] = [];
  private blockFetchPromises: Map<number, Promise<void>> = new Map();
  private backgroundFetchPromise: Promise<void> | null = null;

  constructor(
    private api: VideoApiActions,
    private itemId: string,
    private method: 'get' | 'put',
    private streamId: StreamRoleId,
    private blockSize: number,
    private totalSegmentCount: number,
  ) {}

  async getMediaSegmentUrl(segmentIndex: number): Promise<string> {
    const blockIndex = Math.floor(segmentIndex / this.blockSize);

    // Ensure current and next blocks are loaded
    await this.ensureCurrentAndNextBlocksLoaded(blockIndex);

    // Get URL from the appropriate block
    const url = this.tryGetUrl(segmentIndex);

    invariant(
      url != undefined,
      'Missing url for segment index ' + segmentIndex + ' of stream ' + this.streamId
    );

    return url;
  }

  close(): void {
    // Clear all pending promises
    this.blockFetchPromises.clear();

    // Cancel background fetch if running
    this.backgroundFetchPromise = null;

    // Clear all cached blocks and their data
    this.blocks.forEach(block => {
      if (block) {
        block.urlSuffixes = [];
        block.prefix = '';
      }
    });
    this.blocks = [];
  }


  private async ensureCurrentAndNextBlocksLoaded(blockIndex: number): Promise<void> {
    // Check if current block already exists
    if (this.blocks[blockIndex]) {
      return;
    }

    // Check if already fetching this block or the next block
    const existingPromise = this.blockFetchPromises.get(blockIndex);
    if (existingPromise !== undefined) {
      await existingPromise;
      return;
    }

    // Start new fetch and cache the promise for deduplication of both blocks
    const fetchPromise = this.performBlockFetch(blockIndex);
    this.blockFetchPromises.set(blockIndex, fetchPromise);
    this.blockFetchPromises.set(blockIndex + 1, fetchPromise);

    // Always clean up both promises when done
    void fetchPromise.finally(() => {
      this.blockFetchPromises.delete(blockIndex);
      this.blockFetchPromises.delete(blockIndex + 1);
    });

    await fetchPromise;
  }

  private async performBlockFetch(blockIndex: number): Promise<void> {
    // Fetch current and next blocks
    const firstSegment = blockIndex * this.blockSize;
    const lastSegment = firstSegment + (2 * this.blockSize) - 1;

    const response = await this.api.getUrlArray({
      itemId: this.itemId,
      method: this.method,
      streamId: this.streamId,
      first: firstSegment,
      last: lastSegment,
    });

    // Slice response into blocks
    this.sliceIntoBlocks(response, blockIndex);

    // Trigger background fetch of remaining segments
    // Only if there are actually segments beyond the initial 2-block fetch
    if (this.backgroundFetchPromise === null &&
        this.totalSegmentCount > (blockIndex + 2) * this.blockSize
    ) {
      this.backgroundFetchPromise = this.backgroundFetchRemainingBlocks(blockIndex + 2).catch(() => {
        // Reset promise on error so it can be retried later
        this.backgroundFetchPromise = null;
      });
    }
  }


  private sliceIntoBlocks(response: UrlArrayResponseDto, startBlockIndex: number): void {
    invariant(
      response.first === startBlockIndex * this.blockSize,
      `Response first segment ${response.first} doesn't match expected start ${startBlockIndex * this.blockSize} for block ${startBlockIndex}`
    );

    // Split response into blockSize chunks
    for (let i = 0; i < response.urlSuffixes.length; i += this.blockSize) {
      const blockIndex = startBlockIndex + Math.floor(i / this.blockSize);
      const blockUrlSuffixes = response.urlSuffixes.slice(i, i + this.blockSize);

      this.blocks[blockIndex] = {
        prefix: response.prefix,
        first: response.first + i,
        urlSuffixes: blockUrlSuffixes
      };
    }
  }

  private async backgroundFetchRemainingBlocks(startBlockIndex: number): Promise<void> {
    const firstSegment = startBlockIndex * this.blockSize;
    invariant(
      firstSegment < this.totalSegmentCount,
      `fetchRemainingBlocks called with startBlockIndex ${startBlockIndex} but only ${this.totalSegmentCount} segments available`
    );
    const estimatedTotalBlocks = Math.ceil(this.totalSegmentCount / this.blockSize);
    const lastSegment = (estimatedTotalBlocks * this.blockSize) - 1;

    const response = await this.api.getUrlArray({
      itemId: this.itemId,
      method: this.method,
      streamId: this.streamId,
      first: firstSegment,
      last: lastSegment,
    });

    // Slice response into blocks
    this.sliceIntoBlocks(response, startBlockIndex);
  }

  private tryGetUrl(segmentIndex: number): string | undefined {
    const blockIndex = Math.floor(segmentIndex / this.blockSize);
    if (this.blocks[blockIndex] === undefined) {
      return undefined;
    }
    const block = this.blocks[blockIndex]!; // Safe after undefined check above
    const offset = segmentIndex - block.first;
    invariant(offset >= 0 && offset < block.urlSuffixes.length,
      () => `Index ${segmentIndex} outside bounds of block ${blockIndex} [${block.first};${block.first + block.urlSuffixes.length - 1}]`);
    return `${block.prefix}${block.urlSuffixes[offset]}`;
  }
}


/**
 * Multi-stream URL cache that creates OptimizedItemSegmentUrlCache instances per stream.
 * Manages cleanup of all cached streams when closed.
 */
export class OptimizedSegmentUrlCache implements SegmentUrlResolver {

  private caches: Record<string, OptimizedItemSegmentUrlCache> = {};

  constructor(
    private api: VideoApiActions,
    private method: 'get' | 'put',
    private batchSize: number,
    private totalSegmentCount: number,
  ) {}

  close(): void {
    Object.values(this.caches).forEach(cache => cache.close());
    this.caches = {};
  }

  getMediaSegmentUrl(itemId: string, streamId: StreamRoleId, segmentIndex: number): Promise<string> {
    const key = `${itemId}:${streamId}`;
    if (!this.caches[key]) {
      this.caches[key] = new OptimizedItemSegmentUrlCache(this.api, itemId, this.method, streamId, this.batchSize, this.totalSegmentCount);
    }

    return this.caches[key].getMediaSegmentUrl(segmentIndex);
  }

}
